import React, { type FC, useState } from 'react'
import { InputAdornment, TextField } from '@mui/material'

import { Translation } from '../../../../domain'

import { Button, VisibilityToggle } from '../../common'
import { FormattedTranslation, useTranslator } from '../../Internationalization'

/**
 * Widget for parameters flagged `secret: true` in the parameters schema (write-only values).
 *
 * On update, the field is concealed by default (the API never renders the stored value back);
 * a "Change" action reveals an empty password input. While editing, an eye toggle shows the
 * typed value, and "Cancel" abandons the edit (clearing the typed value) without touching the
 * rest of the dialog. A value that is never entered is omitted from the request — the backend
 * keeps the stored value.
 *
 * On creation there is no stored value yet, so a (required) password input is shown directly.
 *
 * Browser autofill is suppressed on the input so saved credentials don't pop up unexpectedly.
 *
 * Prop types are kept structural (instead of @rjsf/utils' WidgetProps) to not
 * spread the rjsf type dependency further than the Form wrapper
 *
 * Renders @mui/material's TextField directly rather than the common/Input TextField wrapper:
 * that wrapper hardcodes variant="outlined" (this form's fields are all "standard", forced by
 * Form.tsx's theme override) and its endIcon slot renders one decorative icon, not the two
 * interactive controls (visibility toggle, Cancel) this widget's end adornment needs. The
 * visibility toggle itself still reuses the shared `VisibilityToggle` component.
 */
type Props = Readonly<{
    id?: string
    label?: string
    value?: unknown
    required?: boolean
    disabled?: boolean
    readonly?: boolean
    options?: Readonly<{ creating?: unknown }>
    onChange: (value: unknown) => void
}>

export const SecretParameterWidget: FC<Props> = ({ id, label, value, required, disabled, readonly, options, onChange }) => {
    const translator = useTranslator()
    /** The read-only view dialog must not offer any interaction, same as disabled */
    const inactive = Boolean(disabled) || Boolean(readonly)
    const creating = options?.creating === true
    const [revealed, setRevealed] = useState(creating)
    const [showValue, setShowValue] = useState(false)

    if (!revealed) {
        return (
            <TextField
                id={id}
                label={label}
                variant="standard"
                fullWidth
                disabled
                value={'••••••••'}
                helperText={translator.formatTranslation(Translation.SECRET_PARAMETER_CONCEALED_HELPER)}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Button
                                data-testid="secret-parameter-change"
                                variant="primaryGhost"
                                size="small"
                                disabled={inactive}
                                onClick={() => setRevealed(true)}
                            >
                                <FormattedTranslation id={Translation.SECRET_PARAMETER_CHANGE} />
                            </Button>
                        </InputAdornment>
                    ),
                }}
            />
        )
    }

    return (
        <TextField
            id={id}
            label={label}
            type={showValue ? 'text' : 'password'}
            variant="standard"
            fullWidth
            required={creating && required}
            disabled={inactive}
            autoComplete="new-password"
            value={typeof value === 'string' ? value : ''}
            onChange={({ target }) => onChange(target.value === '' ? undefined : target.value)}
            helperText={creating ? undefined : translator.formatTranslation(Translation.SECRET_PARAMETER_EDIT_HELPER)}
            inputProps={{ autoComplete: 'new-password' }}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <VisibilityToggle
                            data-testid="secret-parameter-toggle-visibility"
                            visible={showValue}
                            disabled={inactive}
                            onToggle={() => setShowValue(!showValue)}
                            showLabel={translator.formatTranslation(Translation.SECRET_PARAMETER_SHOW_VALUE)}
                            hideLabel={translator.formatTranslation(Translation.SECRET_PARAMETER_HIDE_VALUE)}
                        />
                        {!creating && (
                            <Button
                                data-testid="secret-parameter-cancel"
                                variant="primaryGhost"
                                size="small"
                                disabled={inactive}
                                onClick={() => {
                                    onChange(undefined)
                                    setShowValue(false)
                                    setRevealed(false)
                                }}
                            >
                                <FormattedTranslation id={Translation.CANCEL} />
                            </Button>
                        )}
                    </InputAdornment>
                ),
            }}
        />
    )
}
